import { useState, type MouseEvent as ReactMouseEvent } from 'react'
import { groupLabels } from '../principleDecisions/responsibleGroups'
import ResponsibleGroups from '../../components/ui/ResponsibleGroups'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import DataTable from '../../components/ui/DataTable'
import Button from '../../components/ui/Button'
import { deleteLabel, rowActionLabel } from '../../lib/deleteLabel'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import {
  emptyItemRow,
  hasDescription,
  responsibleLabels,
  type MinuteItemKind,
  type MinuteItemRow,
} from './types'
import type { EditOp } from '../../lib/editOp'
import MinuteItemFormDialog from './MinuteItemFormDialog'
import PendingFilesDialog from './components/PendingFilesDialog'
import PrincipleFilesDialog from '../principleDecisions/components/PrincipleFilesDialog'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import '../actions/actions.css'
import '../minutes/minutes.css'

interface Props {
  /** Bölüm başlığı (Toplantı Kayıt Tipi'nden — "Gündem"/"Prensip Kararları"/"Kararlar"). */
  title: string
  kind: MinuteItemKind
  value: MinuteItemRow[]
  onChange: (rows: MinuteItemRow[], op: EditOp) => void
  /** No kolonu gösterilsin mi? Tutanak Oluştur'da kayıt henüz üretilmediğinden numara yok → gizlenir (false). */
  showNumber?: boolean
  /**
   * Verilirse satırlar <b>sürükle-bırak</b> ile yeniden sıralanabilir (yalnız Gündem). Yeni sıralı liste döner;
   * çağıran kalıcılaştırır (detayda record_number 1..N yazılır, create'te yerel dizi). Verilmezse sıralama kapalı.
   */
  onReorder?: (rows: MinuteItemRow[]) => void
  /**
   * **Tutanak Oluştur** ekranı mı? (108) Orada kayıt tek seferde yapılır, satırların id'si yoktur ve
   * prensip kararı dosyaları <b>yerelde bekler</b>; çağıran kaydetme dönüşünde yükler.
   *
   * <p>Varsayılan `false` = Toplantı Detay: satır anında kaydedilir, dosya doğrudan sunucuya gider.
   * Ayrım bir prop'tur, satırın id'sinden ÇIKARILMAZ: detayda yeni eklenen satırın id'si senkron
   * dönene kadar bir an boştur ve o anda bekleyen-dosya kutusu açılsaydı seçilen dosyalar sessizce
   * kaybolurdu (kimse onları yüklemiyor).
   */
  pendingFiles?: boolean
}

/**
 * Basit içerik editörü (Prensip Kararı / Karar / Gündem — analiz 1.7–1.9). Eklenen maddeler <b>Toplantı Detay</b>
 * tablosuyla aynı kolonlarda listelenir; ekle/düzenle girişleri karmaşayı önlemek için bir <b>popup</b>'ta
 * ({@link MinuteItemFormDialog}) toplanır. {@link ActionsEditor} ile aynı desen — tek fark skor/termin/durum yok.
 *
 * <h2>Dosya eki — yalnız Prensip Kararı (108)</h2>
 *
 * Satır işlemlerine bir <b>ataç</b> düğmesi eklenir ve iki farklı pop-up açar, çünkü bu editör iki ayrı
 * ekranda çalışıyor ve satırın durumu farklı:
 * <ul>
 *   <li><b>Toplantı Detay</b> — her ekle/düzenle anında senkronlanır, satırın id'si vardır: dosya
 *       doğrudan sunucuya gider ({@link PrincipleFilesDialog}, Prensip Kararları ekranıyla aynı pop-up).
 *   <li><b>Tutanak Oluştur</b> — tüm tutanak tek seferde kaydedilir, satırın id'si <b>yoktur</b>: dosya
 *       satırın yerel durumunda bekler ({@link PendingFilesDialog}) ve kaydetme dönüşünde yüklenir.
 * </ul>
 * Karar/Gündem satırlarında düğme <b>hiç çizilmez</b> — o tiplerde dosya eki yoktur.
 */
export default function MinuteItemsEditor({
  title,
  kind,
  value,
  onChange,
  showNumber = true,
  onReorder,
  pendingFiles = false,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const withDescription = hasDescription(kind)
  // 077 — No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem) VE showNumber true iken.
  const showRecordNumber = useGrants().hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  const removeGroup = `minuteitem-remove-${kind}` // izole onay grubu (diğer editör/onaylarla çakışmaz)
  // Dar ekranda (≤CARD_VIEW_BREAKPOINT) tablo yerine kart listesi — sürükle-bırak sıralama (yalnız
  // Gündem) kartta karşılığı olmadığı için mobilde kapalıdır; ekleme/düzenleme popup'tan değişmez.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<MinuteItemRow>(emptyItemRow)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  /**
   * Dosyalar pop-up'ı açık olan satırın kimliği (`null` → kapalı).
   *
   * <p>Kaydedilmiş satırda **id** tutulur, kaydedilmemişte yerel `key`. Sebep: Toplantı Detay'da
   * senkron yanıtı satırları `itemToRow` ile yeniden üretiyor ve her satıra **yeni bir key** veriyor;
   * key'e bağlansaydı, arka planda biten bir senkron açık pop-up'ı kendiliğinden kapatırdı (kullanıcı
   * dosya seçerken). Id senkron boyunca değişmez.
   */
  const [filesRef, setFilesRef] = useState<FilesRef | null>(null)

  // Dosya eki yalnız Prensip Kararı'nda ve yalnız yetkili kullanıcıda; yetkisiz için düğme hiç çizilmez
  // (uçlar da PRINCIPLE_DECISION_MANAGE istiyor, düğme yalnız 403'e götürürdü).
  const canManageFiles = useGrants().hasGrant(GRANTS.PRINCIPLE_DECISION_MANAGE)
  const showFiles = kind === 'principles' && canManageFiles
  const filesRow = findRowByRef(value, filesRef)
  /**
   * İşlem kolonunun ölçüsü. Prensip Kararı'nda üç düğme var (Dosyalar/Düzenle/Sil) ve satırdan satıra
   * ikon sayısı değişiyor (pasif/aktif ataç) → **sağa dayalı**. Gündem ve Karar tabloları iki düğmeyle
   * **ortalı** kalır; `ui.css`'teki `.ui-file-row-actions` gerekçesi bunu şart koşuyor.
   */
  const actionColumn = showFiles
    ? ({ width: '8rem', align: 'right' } as const)
    : ({ width: '6rem', align: 'center' } as const)
  const openFiles = (row: MinuteItemRow) => setFilesRef({ id: row.id, key: row.key })
  const closeFiles = () => setFilesRef(null)
  /**
   * Satır dosya kabul ediyor mu? Toplantı Detay'da kayıt gerekir (id); Tutanak Oluştur'da dosya zaten
   * yerelde beklediği için id aranmaz. Detayda henüz senkronlanmamış satırda düğme <b>pasif</b> çizilir —
   * gizlemek "neden yok?" sorusunu doğururdu, pasif düğme ipucuyla sebebi söylüyor.
   */
  const filesEnabled = (row: MinuteItemRow) => rowAcceptsFiles(row, pendingFiles)

  /** Bekleyen dosyaları satıra işler (yalnız kaydedilmemiş satırda kullanılır). */
  const setPendingFiles = (row: MinuteItemRow, files: File[]) =>
    onChange(withFiles(value, row, files), 'update')

  const openAdd = () => {
    setDraft(emptyItemRow())
    setEditingKey(null)
    setDialogOpen(true)
  }
  const openEdit = (row: MinuteItemRow) => {
    setDraft(row)
    setEditingKey(row.key)
    setDialogOpen(true)
  }
  const confirmRemove = (row: MinuteItemRow) =>
    confirmDialog({
      group: removeGroup,
      header: t('minuteItems.common.removeConfirm.title'),
      message: t('minuteItems.common.removeConfirm.message', { name: row.name || '—' }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('minuteItems.common.remove'),
      accept: () => onChange(value.filter((r) => r.key !== row.key), 'delete'),
    })

  const handleSave = (row: MinuteItemRow) => {
    onChange(
      editingKey ? value.map((r) => (r.key === editingKey ? row : r)) : [...value, row],
      editingKey ? 'update' : 'add',
    )
    setDialogOpen(false)
  }

  return (
    <section className="minutes-section">
      <div className="minutes-section-head">
        <h2>{title}</h2>
      </div>

      {value.length === 0 ? (
        <p className="act-empty-hint">{t(`minuteItems.${kind}.empty`)}</p>
      ) : isMobile ? (
        <MinuteItemCards
          value={value}
          showNumber={showNumber && showRecordNumber}
          withDescription={withDescription}
          onEdit={openEdit}
          onRemove={confirmRemove}
          showFiles={showFiles}
          onFiles={openFiles}
          filesEnabled={filesEnabled}
        />
      ) : (
        <DataTable
          value={value}
          dataKey="key"
          paginator={false}
          className="minutes-participant-table"
          reorderableRows={!!onReorder}
          onRowReorder={(e) => onReorder?.(e.value)}
        >
          {onReorder && (
            <Column rowReorder headerStyle={{ width: '3rem' }} bodyStyle={{ textAlign: 'center' }} />
          )}
          {showNumber && showRecordNumber && (
            <Column
              header={t(`minuteItems.${kind}.number`)}
              /* Numara sunucuda ATAMA anında üretilir (toplantı grubu kalıbı); Toplantı Detay'da gerçek numara
                 (§12 biçimi) görünür. Tutanak Oluştur'da kayıt henüz üretilmediğinden kolon gizlenir. */
              body={(row: MinuteItemRow) => row.recordNumber || ''}
            />
          )}
          <Column
            field="name"
            header={t(`minuteItems.${kind}.name`)}
            body={(row: MinuteItemRow) => (
              <span className="minutes-cell-strong act-cell-clip act-cell-clip--wide" title={row.name}>
                {row.name || ''}
              </span>
            )}
          />
          {withDescription && (
            <Column
              header={t(`minuteItems.${kind}.descriptionColumn`)}
              body={(row: MinuteItemRow) => (
                <span className="act-cell-clip act-cell-clip--wide" title={row.description}>
                  {row.description || ''}
                </span>
              )}
            />
          )}
          <Column
            header={t(responsibleLabels(kind).label)}
            // `tk-cell-wrap`: global `td { white-space: nowrap }` bu hücrede kapanır — gruplanmış
            // sorumlu metni sarmalı, tabloyu taşırmamalı (push kapısı bulgusu 01.10.2026).
            bodyClassName="tk-cell-wrap"
            body={(row: MinuteItemRow) => (
              // Gösterim Prensip Kararları sayfasıyla ORTAK (kullanıcı isteği 01.10.2026).
              <ResponsibleGroups responsibles={row.responsibles} />
            )}
          />
          <Column
            header=""
            headerStyle={{ width: actionColumn.width }}
            bodyStyle={{ textAlign: actionColumn.align }}
            body={(row: MinuteItemRow) => (
              <RowActions
                row={row}
                showFiles={showFiles}
                filesEnabled={filesEnabled(row)}
                onFiles={openFiles}
                onEdit={openEdit}
                onRemove={confirmRemove}
              />
            )}
          />
        </DataTable>
      )}

      <div className="act-add-bar">
        <Button
          type="button"
          variant="secondary"
          icon="pi pi-plus"
          label={t(`minuteItems.${kind}.add`)}
          onClick={openAdd}
        />
      </div>

      <ConfirmDialog group={removeGroup} />

      <MinuteItemFormDialog
        visible={dialogOpen}
        kind={kind}
        initial={draft}
        isNew={editingKey === null}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />

      {showFiles && (
        <FilesDialogs
          row={filesRow}
          pendingFiles={pendingFiles}
          onPendingChange={setPendingFiles}
          onHide={closeFiles}
        />
      )}
    </section>
  )
}

/**
 * Satır dosya kabul ediyor mu? Toplantı Detay'da kayıt gerekir (id); Tutanak Oluştur'da dosya zaten
 * yerelde beklediği için id aranmaz.
 */
function rowAcceptsFiles(row: MinuteItemRow, pendingFiles: boolean): boolean {
  return pendingFiles || row.id != null
}

/** Bir satırın bekleyen dosyalarını değiştirir; diğer satırlar aynen kalır. */
function withFiles(rows: MinuteItemRow[], row: MinuteItemRow, files: File[]): MinuteItemRow[] {
  return rows.map((r) => (r.key === row.key ? { ...r, files } : r))
}

/** Dosyalar pop-up'ı açık olan satırın kimliği — kaydedilmişte `id`, kaydedilmemişte yerel `key`. */
interface FilesRef {
  id: number | null
  key: string
}

/**
 * Açık pop-up'ın satırını bulur. <b>Id varsa id ile</b> aranır: Toplantı Detay'da senkron yanıtı
 * satırları yeniden üretiyor ve her satıra yeni bir `key` veriyor; key ile aransaydı arka planda biten
 * bir senkron açık pop-up'ı kendiliğinden kapatırdı. Kaydedilmemiş satırda id yoktur, key kararlıdır.
 */
function findRowByRef(rows: MinuteItemRow[], ref: FilesRef | null): MinuteItemRow | null {
  if (ref == null) return null
  if (ref.id != null) return rows.find((r) => r.id === ref.id) ?? null
  return rows.find((r) => r.key === ref.key) ?? null
}

/**
 * Kart görünümündeki Dosyalar düğmesi. Masaüstüyle aynı kural: pasifse sebep hem `title` hem
 * `aria-label` içinde. Kartta düğme aktif görünüp tıklamanın sessizce yutulması "bozuk mu?" hissi
 * veriyordu (push kapısı bulgusu).
 */
function CardFilesButton({
  row,
  enabled,
  onFiles,
}: Readonly<{
  row: MinuteItemRow
  enabled: boolean
  onFiles: (row: MinuteItemRow) => void
}>) {
  const { t } = useTranslation()
  const label = enabled
    ? t('minuteItems.files.open', { name: row.name || '—' })
    : t('minuteItems.files.savingHint')
  return (
    <button
      type="button"
      className="minutes-remove-btn"
      aria-label={label}
      title={label}
      disabled={!enabled}
      onClick={() => onFiles(row)}
    >
      <i className="pi pi-paperclip" aria-hidden="true" />
    </button>
  )
}

/**
 * Masaüstü tablonun <b>satır işlemleri</b>: Dosyalar (yalnız Prensip Kararı + yetki) · Düzenle · Sil.
 *
 * <p>Ayrı bileşen: koşullar ve etiket hesapları ana editörün gövdesindeyken bilişsel karmaşıklık
 * Sonar eşiğine (S3776, 15) dayanmıştı — ölçüldü, tahmin edilmedi.
 */
function RowActions({
  row,
  showFiles,
  filesEnabled,
  onFiles,
  onEdit,
  onRemove,
}: Readonly<{
  row: MinuteItemRow
  showFiles: boolean
  filesEnabled: boolean
  onFiles: (row: MinuteItemRow) => void
  onEdit: (row: MinuteItemRow) => void
  onRemove: (row: MinuteItemRow) => void
}>) {
  const { t } = useTranslation()
  const removeLabel = deleteLabel(t, 'minuteItems.common.remove', row.name)
  // Düzenle de satır adını taşır (THY inceleme bulgusu, 18.09.2026): aynı satırdaki silme ve
  // dosya düğmeleri adı taşırken düzenlemenin sabit "Düzenle" kalması hem ayırt edilemez hem
  // kendi içinde tutarsızdı. Ad boşsa (henüz adı girilmemiş taslak satır) adsız anahtara düşer.
  const editLabel = rowActionLabel(t, 'minuteItems.common.edit', row.name)
  // Etiket satır başına BİR kez (Duo bulgusu b74e970a — aynı dosyada aynı kalıp). Pasif düğmede sebep
  // aria-label'a da girer: ekran okuyucu "devre dışı" der ama NEDEN olduğunu title'dan okuyamaz.
  const filesLabel = filesEnabled
    ? t('minuteItems.files.open', { name: row.name || '—' })
    : t('minuteItems.files.savingHint')
  /** Satır tıklaması/sürükleme ile karışmasın diye her işlem olayı durdurulur. */
  const handler = (fn: (row: MinuteItemRow) => void) => (ev: ReactMouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    fn(row)
  }

  return (
    <div className={`act-row-actions${showFiles ? ' ui-file-row-actions' : ''}`}>
      {showFiles && (
        <button
          type="button"
          className="minutes-remove-btn"
          aria-label={filesLabel}
          title={filesLabel}
          disabled={!filesEnabled}
          onClick={handler(onFiles)}
        >
          <i className="pi pi-paperclip" aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className="minutes-remove-btn"
        aria-label={editLabel}
        title={editLabel}
        onClick={handler(onEdit)}
      >
        <i className="pi pi-pencil" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="minutes-remove-btn"
        aria-label={removeLabel}
        title={removeLabel}
        onClick={handler(onRemove)}
      >
        <i className="pi pi-trash" aria-hidden="true" />
      </button>
    </div>
  )
}

/**
 * Açık satırın dosya pop-up'ı — <b>kaydedilmiş</b> satırda sunucudaki dosyalar, <b>kaydedilmemişte</b>
 * yerelde bekleyenler. Ayrım satırın id'sidir; hangi pop-up'ın <b>var olduğu</b> ise ekranın modudur
 * ({@code pendingFiles}): Toplantı Detay'da bekleyen-dosya kutusu HİÇ açılmaz, çünkü orada o dosyaları
 * yükleyecek kimse yoktur (satır bir an id'siz görünse bile).
 *
 * <p>Ayrı bileşen: koşullar ana editörün gövdesinde dururken bilişsel karmaşıklık ölçümü 14/15'e
 * çıkmıştı (Sonar S3776 — `eslint-plugin-sonarjs` ile ölçüldü, tahmin değil).
 */
function FilesDialogs({
  row,
  pendingFiles,
  onPendingChange,
  onHide,
}: Readonly<{
  row: MinuteItemRow | null
  pendingFiles: boolean
  onPendingChange: (row: MinuteItemRow, files: File[]) => void
  onHide: () => void
}>) {
  if (row == null) return null
  if (row.id != null) {
    return (
      <PrincipleFilesDialog
        decision={{ id: row.id, recordNumber: row.recordNumber, description: row.name }}
        canManage
        onHide={onHide}
      />
    )
  }
  if (!pendingFiles) return null
  return (
    <PendingFilesDialog
      title={row.name || '—'}
      files={row.files ?? []}
      onChange={(files) => onPendingChange(row, files)}
      onHide={onHide}
    />
  )
}

/**
 * Gündem/Prensip/Karar maddelerinin dar ekran karşılığı — masaüstündeki `DataTable`'ın yerine (aynı
 * anda değil) çizilir. `ActionNoteCards` ile aynı desen (tıklanamaz — maddenin detay sayfası yok).
 * Sürükle-bırak sıralama (yalnız Gündem) kartta YOKTUR; sıra değişmesi gerekiyorsa madde düzenleme
 * popup'ından yapılan ekle/sil ile dolaylı olur — bu bilinçli bir kapsam daraltmasıdır.
 */
function MinuteItemCards({
  value,
  showNumber,
  withDescription,
  onEdit,
  onRemove,
  showFiles,
  onFiles,
  filesEnabled,
}: Readonly<{
  value: MinuteItemRow[]
  showNumber: boolean
  withDescription: boolean
  onEdit: (row: MinuteItemRow) => void
  onRemove: (row: MinuteItemRow) => void
  /** Dosyalar düğmesi çizilsin mi (yalnız Prensip Kararı + yetki)? */
  showFiles: boolean
  onFiles: (row: MinuteItemRow) => void
  /** Satır dosya kabul ediyor mu? Masaüstüyle AYNI kural — kartta da pasif çizilir ve sebebi söylenir. */
  filesEnabled: (row: MinuteItemRow) => boolean
}>) {
  const { t } = useTranslation()
  return (
    <div className="ui-card-list minuteitem-cards">
      {value.map((row) => {
        const removeLabel = deleteLabel(t, 'minuteItems.common.remove', row.name)
  // Düzenle de satır adını taşır (THY inceleme bulgusu, 18.09.2026): aynı satırdaki silme ve
  // dosya düğmeleri adı taşırken düzenlemenin sabit "Düzenle" kalması hem ayırt edilemez hem
  // kendi içinde tutarsızdı. Ad boşsa (henüz adı girilmemiş taslak satır) adsız anahtara düşer.
  const editLabel = rowActionLabel(t, 'minuteItems.common.edit', row.name)
        return (
        <div key={row.key} className="ui-card">
          <div className="ui-card-head">
            <span className="ui-card-title">{row.name || ''}</span>
            <div className="ui-card-actions">
              {showFiles && (
                <CardFilesButton row={row} enabled={filesEnabled(row)} onFiles={onFiles} />
              )}
              <button
                type="button"
                className="minutes-remove-btn"
                aria-label={editLabel}
                title={editLabel}
                onClick={() => onEdit(row)}
              >
                <i className="pi pi-pencil" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="minutes-remove-btn"
                aria-label={removeLabel}
                title={removeLabel}
                onClick={() => onRemove(row)}
              >
                <i className="pi pi-trash" aria-hidden="true" />
              </button>
            </div>
          </div>
          {showNumber && row.recordNumber && <span className="ui-card-sub">{row.recordNumber}</span>}
          {withDescription && row.description && (
            <div className="ui-card-row act-card-desc">{row.description}</div>
          )}
          {row.responsibles.length > 0 && (
            <div className="ui-card-row">{groupLabels(row.responsibles)}</div>
          )}
        </div>
        )
      })}
    </div>
  )
}
